import { track } from '@/lib/measurement';
import { accessToken, authHeaders } from './http';

/**
 * The vault ladder API (/api/vault/*.php). Server contract: api/vault/{status,claim,download,_vault}.php,
 * measured in docs/behavior/funnel.md §3.1–3.3.
 */
export const VAULT_SKUS = ['sovereign-stack', 'shadow-king', 'shadow-oracle', 'omega-os'] as const;
export type VaultSku = (typeof VAULT_SKUS)[number];

/** The rungs a visitor pays for; sovereign-stack is free. */
export const PAID_SKUS = ['shadow-king', 'shadow-oracle', 'omega-os'] as const;
export type PaidSku = (typeof PAID_SKUS)[number];

export type BlockedBy = 'signin' | 'prerequisite' | 'payment' | 'unforged' | null;

export interface VaultDocument {
  readonly title: string;
  readonly latin: string;
  readonly route: string;
  /** False while the deliverable does not exist yet: purchase clicks open the waitlist instead. */
  readonly forged: boolean;
  readonly revealed: boolean;
  readonly canDownload: boolean;
  readonly blockedBy: BlockedBy;
  readonly owned: boolean;
  readonly downloaded: boolean;
  readonly completed: boolean;
  readonly requires: string | null;
  readonly deliverable: 'file' | 'access';
}

export type VaultDocuments = Partial<Record<VaultSku, VaultDocument>>;

export interface VaultStatus {
  readonly success: true;
  readonly signedIn: boolean;
  readonly tier: string | null;
  readonly firstName: string | null;
  readonly documents: VaultDocuments;
}

/** claim.php answers with the ladder but without `signedIn` / `firstName`. */
export interface VaultClaim {
  readonly success: true;
  readonly tier: string | null;
  readonly documents: VaultDocuments;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A body is a usable ladder only when it says success and carries a documents object. */
function hasDocuments(body: unknown): body is { success: true; documents: VaultDocuments } {
  return isRecord(body) && body.success === true && isRecord(body.documents);
}

/**
 * GET /api/vault/status.php (Bearer when a token is stored, never cached).
 * Resolves null for a network failure, a non-OK response or a body without documents, so an
 * error body can never replace a good status (FN-D15).
 */
export async function fetchVaultStatus(): Promise<VaultStatus | null> {
  try {
    const response = await fetch('/api/vault/status.php', { headers: authHeaders(), cache: 'no-store' });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (!hasDocuments(body)) return null;
    const record = body as unknown as Record<string, unknown>;
    return {
      success: true,
      signedIn: record.signedIn === true,
      tier: typeof record.tier === 'string' ? record.tier : null,
      firstName: typeof record.firstName === 'string' ? record.firstName : null,
      documents: body.documents,
    };
  } catch {
    return null;
  }
}

/**
 * GET /api/vault/claim.php[?fresh=1] — claims any purchase parked for this account. Only sent with a
 * stored token (the claim is per account); resolves null without a token, on a non-OK response,
 * on a network failure and for a body without documents.
 */
export async function fetchVaultClaim(fresh: boolean): Promise<VaultClaim | null> {
  const token = accessToken();
  if (token === null) return null;
  try {
    const response = await fetch(`/api/vault/claim.php${fresh ? '?fresh=1' : ''}`, {
      headers: authHeaders(token),
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (!hasDocuments(body)) return null;
    const tier = (body as unknown as Record<string, unknown>).tier;
    return { success: true, tier: typeof tier === 'string' ? tier : null, documents: body.documents };
  } catch {
    return null;
  }
}

/** A refused download. `message` is what the button shows after `LOCKED — `. */
export class VaultDownloadError extends Error {
  override readonly name = 'VaultDownloadError';
}

/**
 * GET /api/vault/download.php?doc=<sku> with the Bearer header (a plain link cannot carry it,
 * and a token in the query string would reach the access log). Resolves the PDF and the server's
 * filename. Rejects with the server's `blockedBy`, else its `error`, else `HTTP <status>`;
 * a network failure rejects with the engine's own error.
 */
export async function downloadVaultDocument(sku: VaultSku): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`/api/vault/download.php?doc=${encodeURIComponent(sku)}`, { headers: authHeaders() });
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      throw new VaultDownloadError(`HTTP ${response.status}`);
    }
    const record = isRecord(body) ? body : {};
    const reason = [record.blockedBy, record.error].find((value) => typeof value === 'string' && value !== '');
    throw new VaultDownloadError(typeof reason === 'string' ? reason : `HTTP ${response.status}`);
  }
  track('vault_download', { item_id: sku });
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${sku}.pdf`;
  return { blob: await response.blob(), filename };
}

/** GET /api/proof.php — how many members hold the Stack; null when unknown. */
export async function fetchStackHolders(): Promise<number | null> {
  try {
    const response = await fetch('/api/proof.php', { cache: 'no-store' });
    const body: unknown = await response.json();
    const holders = isRecord(body) ? body.stackHolders : undefined;
    return typeof holders === 'number' && Number.isFinite(holders) ? holders : null;
  } catch {
    return null;
  }
}
